import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  DialogTitle,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
} from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { normalizeInputValue } from "../../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { RoundedButton } from "../../../../common/components/styled/styled";
import { AccessRightContext } from "../../../user/AccessRightProvider";
import {
  DateRange,
  GarnishmentRestriction,
  TalentPaymentProhibition,
} from "./paymentProhibitionAPI";
import {
  createTalentPaymentProhibition,
  deleteTalentPaymentProhibition,
  fetchTalentPaymentProhibitions,
  selectAllPaymentProhibitions,
  updateTalentPaymentProhibition,
  updateTalentPaymentProhibitionCancelDetails,
} from "./paymentProhibitionSlice";
import {
  findCompaniesWithOpenEndDates,
  isSaveDisabled,
  newEmptyItem,
} from "./paymentProhibitionUtils";
import { PaymentProhibitionRowHeader } from "./tableItems/PaymentProhibitionRowHeader";
import { PaymentProhibitionRowItem } from "./tableItems/PaymentProhibitionRowItem";
import isEqual from "../../../../common/helpers/isEqual";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

interface Props {
  talentId: string;
  isReadOnly: boolean;
}

const PaymentProhibition = ({ talentId, isReadOnly }: Props) => {
  const { t } = useTranslation();

  const [paymentProhibitions, setPaymentProhibitions] = useState<
    TalentPaymentProhibition[]
  >([]);
  const [editedItemId, setEditedItemId] = useState<string>();

  const dispatch = useAppDispatch();
  const { canEditUnionPayment } = useContext(AccessRightContext);

  // Get paymentProhibitions from redux adapter
  const storedPaymentProhibitions: TalentPaymentProhibition[] = useAppSelector(
    selectAllPaymentProhibitions,
  );

  const hasBeenEdited = !isEqual(
    paymentProhibitions,
    storedPaymentProhibitions,
  );

  useEffect(() => {
    dispatch(fetchTalentPaymentProhibitions(talentId));
  }, [talentId, dispatch]);

  useEffect(() => {
    setPaymentProhibitions([...storedPaymentProhibitions]);
  }, [storedPaymentProhibitions]);

  const onEditingRow = (rowId: string | null) => {
    setEditedItemId(rowId ? rowId : undefined);
  };

  // Add new empty item
  const addItem = () => {
    const newItem: TalentPaymentProhibition = {
      ...newEmptyItem,
      talentId: talentId,
    };
    setPaymentProhibitions([...paymentProhibitions, newItem]);
    setEditedItemId(newItem.id);
  };

  const deleteRow = (id: string, version: number) => {
    // If deleted item is new one, remove it from paymentProhibitions without dispatching delete
    if (
      paymentProhibitions.find((item) => item.id === id)?.id === editedItemId
    ) {
      setPaymentProhibitions(
        paymentProhibitions.filter((item) => item.id !== id),
      );
      setEditedItemId(undefined);
    } else {
      // Else dispatch delete
      dispatch(deleteTalentPaymentProhibition({ id, version }));
    }
  };

  const onSave = () => {
    const editedItem = paymentProhibitions.find(
      (item) => item.id === editedItemId,
    );

    // Break if no item found
    if (!editedItem) return;

    const normalizedEditedItem: TalentPaymentProhibition = {
      ...editedItem,
      additionalDeduction:
        editedItem?.additionalDeduction &&
        editedItem.additionalDeduction !== "0"
          ? normalizeInputValue(editedItem.additionalDeduction)
          : undefined,
      externalMonthlyNetIncome:
        editedItem?.externalMonthlyNetIncome &&
        editedItem.externalMonthlyNetIncome !== "0"
          ? normalizeInputValue(editedItem.externalMonthlyNetIncome.toString())
          : undefined,
      dailyProtectedPortion:
        editedItem?.dailyProtectedPortion &&
        editedItem.dailyProtectedPortion !== "0"
          ? normalizeInputValue(editedItem.dailyProtectedPortion.toString())
          : undefined,
      deductionTypeAmount:
        editedItem?.deductionTypeAmount &&
        editedItem.deductionTypeAmount !== "0"
          ? normalizeInputValue(editedItem.deductionTypeAmount.toString())
          : undefined,
      garnishmentRestrictions:
        editedItem?.garnishmentRestrictions &&
        editedItem.garnishmentRestrictions.length > 0
          ? editedItem.garnishmentRestrictions.map((restriction) => ({
              ...restriction,
              amount: normalizeInputValue(restriction.amount.toString()),
            }))
          : undefined,
    };

    if (editedItem && editedItemId === "new") {
      // If edited item is new, dispatch create
      dispatch(
        createTalentPaymentProhibition({ ...normalizedEditedItem }),
      ).then(
        (action: any) =>
          action.meta.requestStatus === "fulfilled" &&
          setEditedItemId(undefined),
      );
    } else if (normalizedEditedItem?.type === "SYSTEM") {
      // SYSTEM type of payment prohibition has it's own update endpoint
      // Update is done with type of EXPERT_MANUAL
      dispatch(
        updateTalentPaymentProhibitionCancelDetails({
          id: normalizedEditedItem.id,
          version: normalizedEditedItem.version,
          end: normalizedEditedItem.end,
          type: "EXPERT_MANUAL",
        }),
      ).then(
        (action: any) =>
          action.meta.requestStatus === "fulfilled" &&
          setEditedItemId(undefined),
      );
    } else if (normalizedEditedItem) {
      dispatch(updateTalentPaymentProhibition(normalizedEditedItem)).then(
        (action: any) =>
          action.meta.requestStatus === "fulfilled" &&
          setEditedItemId(undefined),
      );
    }
  };

  const onCancel = () => {
    setPaymentProhibitions([...storedPaymentProhibitions]);
    setEditedItemId(undefined);
  };

  const addException = (rowId: string, type: string) => {
    // Update rows with new exceptions
    const updatedRows = paymentProhibitions.map((rowItem: any) => {
      if (rowItem?.id === rowId) {
        // Copy the current rowItem
        let updatedRow = { ...rowItem };

        // Check the type and update the corresponding array
        if (type === "freeMonth") {
          const newFreeMonth: DateRange = { id: nanoid(), start: "", end: "" };
          // Ensure freeMonth array exists, if not initialize it
          const updatedFreeMonths: DateRange[] = updatedRow.freeMonths
            ? [...updatedRow.freeMonths, newFreeMonth]
            : [newFreeMonth];

          updatedRow = { ...updatedRow, freeMonths: updatedFreeMonths };
        } else if (type === "garnishmentRestriction") {
          const newGarnishmentRestriction: GarnishmentRestriction = {
            id: nanoid(),
            start: "",
            end: "",
            amount: "",
          };
          // Ensure garnishmentRestrictions array exists, if not initialize it
          const updatedGarnishmentRestrictions: GarnishmentRestriction[] =
            updatedRow.garnishmentRestrictions
              ? [
                  ...updatedRow.garnishmentRestrictions,
                  newGarnishmentRestriction,
                ]
              : [newGarnishmentRestriction];

          updatedRow = {
            ...updatedRow,
            garnishmentRestrictions: updatedGarnishmentRestrictions,
          };
        }

        return updatedRow;
      } else {
        return rowItem;
      }
    });
    editedItemId && onEditingRow(editedItemId);
    // Update state
    setPaymentProhibitions([...updatedRows]);
  };

  const isSystemItem =
    paymentProhibitions?.find((item) => item.id === editedItemId)?.type ===
    "SYSTEM";

  const editedItem = paymentProhibitions.find((pp) => pp.id === editedItemId);

  // System can't have open paymentProhibition for the same company
  // Get list of companies from paymentProhibitions that have open end date or date is in the future and conflicts with the editedItem's date range
  const existingCompaniesWithOpenEndDates = findCompaniesWithOpenEndDates(
    paymentProhibitions,
    editedItem,
  );

  return (
    <Box
      sx={{
        background: "#FFFFFF",
        padding: "0px 0px 16px 16px",
        borderRadius: "12px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          padding: "16px 16px 0px 8px",
        }}
      >
        <DialogTitle
          sx={{
            padding: "0px",
            background: "#FFFFFF",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            fontSize: "14px",
          }}
        >
          <Typography
            variant="header"
            sx={{
              fontWeight: 600,
            }}
          >
            {t("talentProfile:paymentProhibition").toUpperCase()}
          </Typography>
        </DialogTitle>
      </Box>

      <>
        <TableContainer>
          <UnsavedChangesProvider isDirty={hasBeenEdited}>
            <Table>
              <TableHead>
                <PaymentProhibitionRowHeader />
              </TableHead>
              <TableBody>
                {paymentProhibitions.map((item, i) => (
                  <PaymentProhibitionRowItem
                    key={i}
                    item={item}
                    onDelete={() => deleteRow(item.id, item.version)}
                    paymentProhibitions={paymentProhibitions}
                    setPaymentProhibitions={setPaymentProhibitions}
                    editedItemId={editedItemId}
                    onEditingRow={onEditingRow}
                    existingCompaniesWithOpenEndDates={
                      existingCompaniesWithOpenEndDates
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </UnsavedChangesProvider>
        </TableContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            padding: "16px 16px 0px 8px",
          }}
        >
          {
            // If there is no edited item, show add button. Otherwise show add exception buttons
            !editedItemId ? (
              canEditUnionPayment && (
                <RoundedButton
                  variant="outlined"
                  onClick={addItem}
                  disabled={isReadOnly}
                >
                  <AddIcon sx={{ marginRight: 1 }} />
                  {t("common:add")}
                </RoundedButton>
              )
            ) : (
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Button
                  sx={{
                    borderRadius: "15px",
                    height: "30px",
                    marginRight: "8px",
                  }}
                  disabled={isSystemItem}
                  startIcon={<AddIcon />}
                  onClick={() => addException(editedItemId, "freeMonth")}
                  variant="outlined"
                >
                  {t("talentProfile:addFreeMonth")}
                </Button>
                <Button
                  sx={{
                    borderRadius: "15px",
                    height: "30px",
                    marginRight: "8px",
                  }}
                  disabled={isSystemItem}
                  startIcon={<AddIcon />}
                  onClick={() =>
                    addException(editedItemId, "garnishmentRestriction")
                  }
                  variant="outlined"
                >
                  {t("talentProfile:addGarnishmentRestriction")}
                </Button>
              </Box>
            )
          }

          <Box>
            {
              // If there is an edited item, show save and cancel buttons
              editedItemId && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    sx={{
                      borderRadius: "15px",
                      height: "30px",
                      marginRight: "8px",
                      color: "red",
                      borderColor: "red",
                    }}
                    startIcon={<ClearIcon />}
                    onClick={onCancel}
                    variant="outlined"
                  >
                    {t("common:cancel")}
                  </Button>
                  <Button
                    disabled={isSaveDisabled(
                      paymentProhibitions,
                      storedPaymentProhibitions,
                      editedItem,
                    )}
                    sx={{ borderRadius: "15px", height: "30px" }}
                    startIcon={<CheckIcon />}
                    onClick={onSave}
                    variant="contained"
                  >
                    {t("common:save")}
                  </Button>
                </Box>
              )
            }
          </Box>
        </Box>
      </>
    </Box>
  );
};

export default PaymentProhibition;
